import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {NodeProps, useStore} from 'reactflow';

import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStep} from 'pipelines/PipelineTypes';
import {
  getExpectedStepDataKind,
  getInputStepDataShape,
  getOutputStepDataShape,
} from 'pipelines/StepDataUtils';
import {getIconForStepDataKind} from 'pipelines/StepIconUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BenchlingNode} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNode';
import removeNulls from 'util/removeNulls';

import {AnalysisFlowchartAction} from './actions/AnalysisFlowchartActionTypes';
import {AnalysisGraphNodeData} from './analysis-graph/AnalysisGraphTypes';
import {useAnalysisFlowchartContext} from './AnalysisFlowchartContext';
import StepDataHandle from './handles/StepDataHandle';
import getStepNodeContents from './node-contents/getStepNodeContents';
import StepPopover from './popovers/StepPopover';

type AnalysisFlowchartNodeProps = Pick<
  NodeProps<AnalysisGraphNodeData>,
  'data' | 'dragging' | 'id' | 'xPos' | 'yPos'
>;

/**
 * Check if the input or output step datas section should be hidden.
 *
 * An input or output step datas section is hidden if there is a single step data and the step only processes
 * data frames.
 */
export function hideStepDatasSection(step: PipelineStep, role: 'INPUT' | 'OUTPUT'): boolean {
  const stepDatas = role === 'INPUT' ? step.inputStepDatas : step.outputStepDatas;
  if (stepDatas.length === 0) {
    return true;
  }
  if (stepDatas.length > 1) {
    return false;
  }
  return [...step.inputStepDatas, ...step.outputStepDatas].every(
    (stepData) => getExpectedStepDataKind(step, stepData) === PipelineStepDataKind.DATASET
  );
}

export default function AnalysisFlowchartNode({
  id: nodeId,
  data: {step},
  dragging,
}: AnalysisFlowchartNodeProps): JSX.Element {
  const isConnecting = useStore((state) => Boolean(state.connectionNodeId));
  const {mode, getActionInfo, highlightedStepIds, selectedNodeIds, setSelectedNodeIds} =
    useAnalysisFlowchartContext();
  const isDimmed = highlightedStepIds.size > 0 && !highlightedStepIds.has(step.id);
  const isSelected = selectedNodeIds.has(nodeId);

  const {nodeName, nodeIcon, nodeStatus, nodeSecondaryInfo} = getStepNodeContents(step, mode);

  const hideInputStepDatasSection = hideStepDatasSection(step, 'INPUT');
  const hideOutputStepDatasSection = hideStepDatasSection(step, 'OUTPUT');

  const sections = removeNulls([
    hideInputStepDatasSection
      ? null
      : {
          leftHeader: 'Inputs',
          sectionRows: step.inputStepDatas.map((stepData) => ({
            leftHandle: <StepDataHandle step={step} stepData={stepData} />,
            primaryText: getInputStepDataShape(step.stepType, step.stepVersion, stepData.name).displayName,
            secondaryText: '',
            icon: getIconForStepDataKind(getExpectedStepDataKind(step, stepData)),
          })),
        },
    hideOutputStepDatasSection
      ? null
      : {
          leftHeader: 'Outputs',
          sectionRows: step.outputStepDatas.map((stepData) => ({
            rightHandle: <StepDataHandle step={step} stepData={stepData} />,
            primaryText: getOutputStepDataShape(step.stepType, step.stepVersion, stepData.name).displayName,
            secondaryText: '',
            icon: getIconForStepDataKind(getExpectedStepDataKind(step, stepData)),
          })),
        },
  ]);

  return (
    <StepPopover step={step} disabled={isSelected || isConnecting || dragging}>
      <div
        className={css(styles.node, isDimmed && styles.dimmedNode)}
        data-dimmed={isDimmed}
        data-selected={isSelected}
        data-step-popover-disabled={isSelected || isConnecting || dragging}
        aria-current={highlightedStepIds.has(step.id) || undefined}
        {...getTestProps({component: 'AnalysisFlowchartNode', element: 'root', key: nodeId})}
        onClick={(e) => {
          // Reserve Shift+Click for multi-selecting nodes, versus opening step configuration.
          if (!(e.shiftKey || e.metaKey || e.ctrlKey)) {
            getActionInfo(AnalysisFlowchartAction.OPEN_STEP_CONFIGURATION, step.id).doAction?.(step.id);
          }
        }}
        onContextMenu={(e) => {
          // Select the node on right-click, so `<AnalysisFlowchartToolbar>` appears for this node.
          setSelectedNodeIds(new Set([nodeId]));
          e.preventDefault();
        }}
      >
        <BenchlingNode.Standard.Node
          headerProps={{
            title: nodeName,
            leftIcon: nodeIcon,
            subTitle: nodeSecondaryInfo,
            leftHandle:
              hideInputStepDatasSection && step.inputStepDatas.length === 1 ? (
                <StepDataHandle step={step} stepData={step.inputStepDatas[0]} />
              ) : null,
            rightHandle:
              hideOutputStepDatasSection && step.outputStepDatas.length === 1 ? (
                <StepDataHandle step={step} stepData={step.outputStepDatas[0]} />
              ) : null,
          }}
          isSelected={isSelected}
          status={nodeStatus}
          sectionsProps={sections.length > 0 ? {sections} : undefined}
        />
      </div>
    </StepPopover>
  );
}

const styles = StyleSheet.create({
  node: {
    wordBreak: 'break-word', // `Float` applies `break-all`, which breaks in the middle of words
  },
  dimmedNode: {
    opacity: 0.3,
    transition: 'opacity 200ms ease-in-out',
  },
});
